const PAIRING_TOKEN_PARAM = "token";

function readHashParams(url: URL): URLSearchParams {
  return new URLSearchParams(url.hash.startsWith("#") ? url.hash.slice(1) : url.hash);
}

export function getPairingTokenFromUrl(url: URL): string | null {
  const hashToken = readHashParams(url).get(PAIRING_TOKEN_PARAM)?.trim() ?? "";
  if (hashToken.length > 0) {
    return hashToken;
  }

  const searchToken = url.searchParams.get(PAIRING_TOKEN_PARAM)?.trim() ?? "";
  return searchToken.length > 0 ? searchToken : null;
}

export function stripPairingTokenFromUrl(url: URL): URL {
  const next = new URL(url.toString());
  const hashParams = readHashParams(next);
  if (hashParams.has(PAIRING_TOKEN_PARAM)) {
    hashParams.delete(PAIRING_TOKEN_PARAM);
    next.hash = hashParams.toString();
  }
  next.searchParams.delete(PAIRING_TOKEN_PARAM);
  return next;
}

export function setPairingTokenOnUrl(url: URL, credential: string): URL {
  const next = new URL(url.toString());
  next.searchParams.delete(PAIRING_TOKEN_PARAM);
  next.hash = new URLSearchParams([[PAIRING_TOKEN_PARAM, credential]]).toString();
  return next;
}

export function resolveDesktopPairingUrl(endpointUrl: string, credential: string): string {
  const url = new URL(endpointUrl);
  url.pathname = "/login";
  return setPairingTokenOnUrl(url, credential).toString();
}

export function resolveCurrentOriginPairingUrl(credential: string): string {
  const url = new URL("/login", window.location.href);
  return setPairingTokenOnUrl(url, credential).toString();
}

/**
 * The deep link a pairing QR encodes.
 *
 * The parameter name is `host`, and it is not a free choice. The only thing
 * that reads these codes is the mobile app, through `readHostedPairingRequest`
 * in `packages/t3-shared/src/remote.ts`, which requires `host` and `token` and
 * returns null without both. This wrote `serverUrl` instead, which nothing has
 * ever read, so the address was dropped: the phone fell through to its raw-URL
 * branch, cleared the path and query, and took the scheme itself as the server.
 * Running the phone's own parser over what this produced resolved the host to
 * the literal string `forge://`, so a scanned code could not connect and
 * presumably never has.
 */
const MOBILE_PAIRING_HOST_PARAM = "host";

export function resolveMobilePairingUrl(pairingUrl: string): string {
  const url = new URL(pairingUrl);
  const token = getPairingTokenFromUrl(url);
  const mobileUrl = new URL("forge:///pair");
  mobileUrl.searchParams.set(MOBILE_PAIRING_HOST_PARAM, url.origin);
  if (token) {
    mobileUrl.searchParams.set(PAIRING_TOKEN_PARAM, token);
  }
  return mobileUrl.toString();
}
